import { inject, Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import {
  BsAuthService,
  BsCacheService,
  BsHubService,
  BsLoggerService,
  UserAttributesInterface
} from '@brightside-web/desktop/data-access/core-services';

import { ConsoleLogger } from 'aws-amplify/utils';
import { FirebaseService } from './firebase.service';
import { HubCapsule } from 'aws-amplify/utils';

import { AmplitudeService } from './amplitude.service';
import * as Sentry from '@sentry/angular';
import { FeatureFlagService } from '@brightside-web/desktop/data-access/core-services';
import { Router } from '@angular/router';
import airbridge from 'airbridge-web-sdk-loader';
import { ServiceInventoryService } from '@brightside-web/desktop/data-access/service-inventory';
import { HAS_CHECKED_ELIGIBILITY_DATA_COLLECT_KEY } from './required-verification.service';
declare const Sprig: (command: string) => void;
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  redirectUrl: string;
  logger: ConsoleLogger;
  username: string | undefined;
  password: string | undefined;
  company: string | undefined;

  private userLoggedIn = new Subject<boolean>();
  private _authEventGeneration = 0;

  private _featureFlagSvc: FeatureFlagService = inject(FeatureFlagService);
  private _bsAuthService: BsAuthService = inject(BsAuthService);
  private _bsCacheService: BsCacheService = inject(BsCacheService);
  private _bsHubService: BsHubService = inject(BsHubService);
  private _bsLoggerService: BsLoggerService = inject(BsLoggerService);
  private _analytics = inject(FirebaseService);
  private _amplitudeService = inject(AmplitudeService);
  private _router = inject(Router);
  private _siService = inject(ServiceInventoryService);

  constructor() {
    this._bsAuthService.fetchUserAttributes().subscribe({
      next: (attributes) => {
        const userAtts = attributes;
        this._featureFlagSvc.initializeFeatureFlag(userAtts);
        this.userLoggedIn.next(true);
      },
      error: () => this.userLoggedIn.next(false),
    });

    this.logger = new ConsoleLogger('My-Logger');
    const listener = (data: HubCapsule<any, any>) => {
      switch (data.payload.event) {
        case 'signedIn': {
          const authEventGeneration = ++this._authEventGeneration;
          this._bsCacheService.removeItem('homeFeedLoadedOnce');
          this._bsCacheService.removeItem(HAS_CHECKED_ELIGIBILITY_DATA_COLLECT_KEY);
          this._bsCacheService.removeItem('hasCheckedProductRegistration');
          this.logger.info('user signed in');
          this._applyAmplitudeAuthId();
          this._bsAuthService.fetchUserAttributes().subscribe((attributes) => {
            if (authEventGeneration !== this._authEventGeneration) {
              return;
            }
            this._bsHubService.dispatch('AuthChannel', { event: 'logged in', data: attributes });
            void this.handleSignIn(attributes['guid'], authEventGeneration)
              .then((signInIsCurrent) => {
                if (signInIsCurrent) {
                  return this._router.navigate(['home']);
                }
                return false;
              })
              .catch((error) => {
                if (authEventGeneration === this._authEventGeneration) {
                  this._siService.resetInventory();
                }
                Sentry.captureException(error, { tags: { operation: 'sign-in-state-reset' } });
              });
            this.setFirebaseProperties(attributes as unknown as { [key: string]: string });
            this.setAmplitudeProperties(attributes as unknown as { [key: string]: string });
            this.setUserLoggedIn(true);
            Sentry.setUser({ id: attributes['guid'] });
            this._featureFlagSvc.initializeFeatureFlag(attributes);
            const signInType = BsAuthService.consumePendingSignInType();
            if (signInType) {
              this._analytics.logEvent('sign_in_completed', { type: signInType });
            }
          });
          break;
        }
        case 'signUp':
          this.logger.info('user signed up');
          break;
        case 'oAuthSignOut':
        case 'signedOut':
          this._authEventGeneration++;
          if (typeof Sprig !== 'undefined') {
            Sprig('logoutUser');
          }
          BsAuthService.clearLastSignInType();
          this._bsCacheService.removeItem('GUID');
          this._bsCacheService.removeItem('ONBOARDING_COMPLETE');
          this._bsCacheService.removeItem('ONBOARDING_FLOW_ACTIVE');
          this._siService.resetInventory();
          this.logger.info(`user ${data.payload.event}` as any);
          this._analytics.clearProperties();
          this._amplitudeService.clearProperties();
          this.setUserLoggedIn(false);
          Sentry.setUser(null);
          this._bsHubService.dispatch('AuthChannel', { event: 'logged out' });
          this._featureFlagSvc.disableClient();
          airbridge.clearUser()
          break;
        case 'signIn_failure':
          this.logger.info('user sign in failed');
          break;
        case 'configured':
          this.logger.info('the Auth module is configured');
          break;
        default:
          this.logger.info(`unhandled Auth event: ${data.payload.event}`);
      }
    };

    this._bsHubService.listen('bsAuth', listener);
  }
  setRedirectUrl(path: string) {
    this.redirectUrl = path;
  }
  private async handleSignIn(guid: string, authEventGeneration: number): Promise<boolean> {
    this._siService.resetInventory();
    await Promise.all([
      this._bsCacheService.removeItem('ONBOARDING_COMPLETE'),
      this._bsCacheService.removeItem('ONBOARDING_FLOW_ACTIVE'),
    ]);
    if (authEventGeneration !== this._authEventGeneration) {
      return false;
    }

    await this._bsCacheService.setItem('GUID', guid);
    if (authEventGeneration !== this._authEventGeneration) {
      await this._bsCacheService.removeItem('GUID');
      return false;
    }

    if (guid) {
      this._siService.loadInventory('onboarding');
    }

    const now = Date.now();

    const previousSignIn = window.localStorage[guid];

    this._bsCacheService.getItem('SIGN_IN_TIME').then((signInTime) => {
      if (authEventGeneration !== this._authEventGeneration) {
        return;
      }
      if (!previousSignIn || previousSignIn === signInTime) {
        window.localStorage[guid] = now;
        this._bsCacheService.setItem('SIGN_IN_TIME', now);
        this._bsCacheService.setItem('FIRST_SIGN_IN', true);
      } else {
        this._bsCacheService.setItem('FIRST_SIGN_IN', false);
      }
    });
    return true;
  }

  /**
   * want to set more than just guid.
   * @param properties
   */
  setFirebaseProperties(properties: { [key: string]: string }) {
    this._analytics.setUserId(properties['guid']);
    const passThrough = {
      company: properties['company'],
    };
    this._analytics.setProperties(passThrough);

    this._analytics.setupRemoteConfig();
  }

  private _applyAmplitudeAuthId(): void {
    this._bsAuthService.getAuth0UserId$().subscribe((authId) => {
      if (authId) {
        this._amplitudeService.setUserProperties({ 'auth id': authId });
      }
    });
  }

  setAmplitudeProperties(properties: { [key: string]: string }) {
    if (properties['guid']) {
      this._amplitudeService.setUserId(properties['guid']);
    }
    const passThrough: Record<string, string> = {};
    if (properties['company']) {
      passThrough.company = properties['company'];
    }

    this._bsAuthService.getAuth0UserId$().subscribe((authId) => {
      if (authId) {
        passThrough['auth id'] = authId;
      }
      if (Object.keys(passThrough).length) {
        this._amplitudeService.setUserProperties(passThrough);
      }
    });
  }

  setUserLoggedIn(userLoggedIn: boolean) {
    this.userLoggedIn.next(userLoggedIn);
  }

  getUserLoggedIn(): Observable<boolean> {
    return this.userLoggedIn.asObservable();
  }

  hasCurrentUser(): Observable<boolean> {
    return this._bsAuthService.isAuthenticated();
  }

  clearConfirmationState() {
    this.username = undefined;
    this.password = undefined;
  }

  private setAirbridgeUser(attributes: UserAttributesInterface) {
    airbridge.setUserID(attributes['guid']);
    airbridge.setUserEmail(attributes['email']);
    airbridge.setUserPhone(attributes['phone_number']);
  }
}
